import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';

/**
 * Rabel Mode: the gym, girly AF. Hot pink everything, pink flames, a sparkle
 * burst on every set ticked and copy with attitude.
 *
 * A theme laid over the plain gym rather than a second copy of it: the
 * .gym-rabel class below re-colours the Tailwind classes the gym uses, and
 * `useRabel().say(plain, rabel)` picks the words. Remembered per phone; it
 * switches itself on the first time Rachel opens the gym on a phone.
 */

const RACHEL = '17249ad3-546d-4a87-b334-fc095bceaad6';
const KEY = 'gym_rabel_mode';

const RabelContext = createContext({ on: false, toggle: () => {}, say: (plain) => plain });
export const useRabel = () => useContext(RabelContext);

export function RabelProvider({ userId, children }) {
  const [on, setOn] = useState(() => {
    try {
      const saved = localStorage.getItem(KEY);
      if (saved != null) return saved === '1';
    } catch { /* private mode: the default, then */ }
    return userId === RACHEL;
  });
  useEffect(() => {
    try { localStorage.setItem(KEY, on ? '1' : '0'); } catch { /* not remembered */ }
  }, [on]);

  // The handwriting font, only when she is in it.
  useEffect(() => {
    if (!on || document.getElementById('gym-rabel-font')) return;
    const link = document.createElement('link');
    link.id = 'gym-rabel-font';
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Pacifico&display=swap';
    document.head.appendChild(link);
  }, [on]);

  const value = { on, toggle: () => setOn(v => !v), say: (plain, rabel) => (on ? rabel : plain) };
  return <RabelContext.Provider value={value}>{children}</RabelContext.Provider>;
}

/** The switch. */
export function RabelToggle({ compact = false }) {
  const { on, toggle } = useRabel();
  return (
    <button
      onClick={toggle}
      className={`flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-semibold border transition ${on ? 'rabel-toggle-on' : 'bg-[#141414] border-[#2a2a2a] text-gray-400'}`}
      aria-pressed={on}
    >
      <span className={`relative inline-block w-8 h-[18px] rounded-full transition ${on ? 'bg-white/90' : 'bg-[#333]'}`}>
        <span className={`absolute top-[2px] w-[14px] h-[14px] rounded-full transition-all ${on ? 'left-[16px] bg-[#ff2d95]' : 'left-[2px] bg-gray-500'}`} />
      </span>
      {!compact && <span>Rabel Mode</span>}
      <span aria-hidden>{on ? '💅' : '🔥'}</span>
    </button>
  );
}

const SASS = [
  'SLAY 💅', 'That\'s hot 🔥', 'Obsessed 💖', 'Main character energy ✨', 'Gravity who? 💁‍♀️',
  'Absolutely ate 🍒', 'Iconic behaviour 👑', 'Not today, weights 💋', 'Snatched 💗', 'She did THAT 💥',
];
export const sassLine = () => SASS[Math.floor(Math.random() * SASS.length)];

/** A burst of hearts and sparkles from a ticked set, with a line of sass. */
export function Burst({ burst }) {
  if (!burst) return null;
  const bits = ['💖', '✨', '💗', '🔥', '💕', '✨', '💖', '🌸', '💗', '✨'];
  return (
    <div key={burst.id} className="pointer-events-none fixed inset-0 z-50 flex items-center justify-center">
      {bits.map((b, i) => (
        <span
          key={i}
          className="rabel-bit absolute text-3xl"
          style={{ '--dx': `${Math.cos((i / bits.length) * Math.PI * 2) * 140}px`, '--dy': `${Math.sin((i / bits.length) * Math.PI * 2) * 140 - 60}px`, animationDelay: `${i * 15}ms` }}
        >
          {b}
        </span>
      ))}
      <span className="rabel-sass absolute text-4xl text-white">{burst.text}</span>
    </div>
  );
}

/*
 * Hot-rod flames, the kind painted on a bonnet: tongues rising out of a solid
 * pink base, with the dark between them a teardrop (a round bulb low down,
 * pinching to a neck) and each tongue an S-wave ending in a long hooked point,
 * the whole outline one white pinstripe. Each tongue is a spine with a width
 * along it; tongue units are the spacing between tongues, y up from the
 * valley line. Drawn at the screen's own pixel size so they never stretch.
 */

// The spine of one tongue, base to tip, leaning right (mirrored for left-leaners).
const SPINE = [
  [0, 0], [-0.02, 0.6], [0.025, 1.35], [-0.015, 2.1], [0.1, 2.75], [0.38, 3.2], [0.8, 3.45],
];

// Half-width along the spine (s = 0 base .. 1 tip): pinched where the dark bulbs
// sit, swelling where they neck, then a long fine point. A smooth curve through
// the keys, so the edges flow with no shoulders.
const WIDTH = [[0, 0.16], [0.1, 0.22], [0.26, 0.355], [0.42, 0.3], [0.6, 0.175], [0.8, 0.075], [1, 0]];
function halfWidth(s) {
  const k = WIDTH;
  let i = 1;
  while (i < k.length - 1 && s > k[i][0]) i++;
  const p0 = k[Math.max(0, i - 2)];
  const p1 = k[i - 1];
  const p2 = k[i];
  const p3 = k[Math.min(k.length - 1, i + 1)];
  const u = Math.min(1, Math.max(0, (s - p1[0]) / (p2[0] - p1[0])));
  // Catmull-Rom on the widths, with slopes scaled to each key's spacing
  const m1 = ((p2[1] - p0[1]) / (p2[0] - p0[0] || 1)) * (p2[0] - p1[0]);
  const m2 = ((p3[1] - p1[1]) / (p3[0] - p1[0] || 1)) * (p2[0] - p1[0]);
  const u2 = u * u;
  const u3 = u2 * u;
  return Math.max(0, (2 * u3 - 3 * u2 + 1) * p1[1] + (u3 - 2 * u2 + u) * m1 + (-2 * u3 + 3 * u2) * p2[1] + (u3 - u2) * m2);
}

function catmull(pts, n) {
  const out = [];
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i - 1] || pts[i];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[i + 2] || p2;
    for (let k = 0; k < n; k++) {
      const t = k / n;
      const t2 = t * t;
      const t3 = t2 * t;
      const f = (a, b, c, d) => 0.5 * (2 * b + (-a + c) * t + (2 * a - 5 * b + 4 * c - d) * t2 + (-a + 3 * b - 3 * c + d) * t3);
      out.push([f(p0[0], p1[0], p2[0], p3[0]), f(p0[1], p1[1], p2[1], p3[1])]);
    }
  }
  out.push(pts[pts.length - 1]);
  return out;
}

/** One tongue's outline: left edge up, right edge down, in tongue units. */
function tongue(height, lean) {
  const spine = catmull(SPINE.map(([x, y]) => [x * lean, y * height]), 24);
  // arc length, so the width profile follows the shape
  const len = [0];
  for (let i = 1; i < spine.length; i++) len.push(len[i - 1] + Math.hypot(spine[i][0] - spine[i - 1][0], spine[i][1] - spine[i - 1][1]));
  const total = len[len.length - 1];
  const left = [];
  const right = [];
  spine.forEach((p, i) => {
    const a = spine[Math.max(0, i - 1)];
    const b = spine[Math.min(spine.length - 1, i + 1)];
    const tx = b[0] - a[0];
    const ty = b[1] - a[1];
    const m = Math.hypot(tx, ty) || 1;
    const nx = -ty / m;
    const ny = tx / m;
    const w = halfWidth(len[i] / total);
    left.push([p[0] + nx * w, p[1] + ny * w]);
    right.push([p[0] - nx * w, p[1] - ny * w]);
  });
  return { left, right: right.reverse() };
}

const HEIGHTS = [0.72, 1, 0.8, 0.94, 0.66, 0.9, 0.78, 1];
// How far down each bulb reaches, as a share of its width.
const DEPTHS = [0.55, 0.9, 0.68, 1.05, 0.62, 0.84];

/** The whole band as an SVG path at pixel size: a solid base, tongues rising out of it. */
function flamePath(width, height) {
  const unit = Math.max(60, Math.min(84, width / 5.6)); // px between tongues
  const base = height - unit * 0.62 - 12; // where the bulbs start, leaving a solid band under the deepest
  const tall = (base - 6) / 3.45 / unit; // the tallest tongue's height factor
  const mid = width / 2;
  const n = Math.ceil(mid / unit) + 2;
  const r = (v) => Math.round(v * 10) / 10;
  const P = ([x, y], cx) => `${r(cx + x * unit)} ${r(base - y * unit)}`;
  let d = '';
  let lastRight = null;
  for (let i = -n; i <= n; i++) {
    const cx = mid + (i + 0.5) * unit;
    const share = HEIGHTS[(i + 64) % HEIGHTS.length];
    const lean = (cx < mid ? -1 : 1) * (0.25 + 0.75 * share ** 3); // a short tongue hooks less, so it stays clear of its neighbour
    const { left, right } = tongue(tall * share, lean);
    const start = left[0];
    if (lastRight == null) d += `M ${r(cx + start[0] * unit)} ${height} L ${P(start, cx)}`;
    else {
      // the round bulb of the dark teardrop between this tongue and the last, at staggered depths
      const [x0, y0] = lastRight;
      const x1 = cx + start[0] * unit;
      const dip = (x1 - x0) * DEPTHS[(i + 64) % DEPTHS.length];
      d += ` C ${r(x0)} ${r(y0 + dip)}, ${r(x1)} ${r(base - start[1] * unit + dip)}, ${P(start, cx)}`;
    }
    for (const p of left.slice(1)) d += ` L ${P(p, cx)}`;
    for (const p of right.slice(1)) d += ` L ${P(p, cx)}`;
    const end = right[right.length - 1];
    lastRight = [cx + end[0] * unit, base - end[1] * unit];
  }
  return `${d} L ${r(lastRight[0])} ${height} Z`;
}

/** Hot-rod flames along the bottom of the screen, behind everything. */
export function Flames() {
  const box = useRef(null);
  const [size, setSize] = useState(null);
  useEffect(() => {
    const el = box.current;
    if (!el) return undefined;
    const measure = () => setSize({ w: el.clientWidth, h: el.clientHeight });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const d = useMemo(() => (size && size.w > 0 && size.h > 40 ? flamePath(size.w, size.h) : null), [size]);
  return (
    <div ref={box} className="pointer-events-none fixed inset-x-0 bottom-0 h-[30vh] max-h-[260px] z-0 overflow-hidden" aria-hidden>
      <div className="rabel-glow absolute inset-x-0 bottom-0 h-full" />
      {d && (
        <svg className="rabel-hood absolute inset-0" width={size.w} height={size.h} viewBox={`0 0 ${size.w} ${size.h}`}>
          <defs>
            <linearGradient id="hotrod-paint" x1="0" x2="0" y1="1" y2="0">
              <stop offset="0" stopColor="#a10f62" />
              <stop offset="0.3" stopColor="#e3157f" />
              <stop offset="0.65" stopColor="#ff3d9e" />
              <stop offset="1" stopColor="#ff8fd2" />
            </linearGradient>
            {/* a glint of light sliding across the paint */}
            <linearGradient id="hotrod-glint" gradientUnits="userSpaceOnUse" x1="0" x2="180" y1="0" y2="-60">
              <stop offset="0" stopColor="#fff" stopOpacity="0" />
              <stop offset="0.5" stopColor="#fff" stopOpacity="0.32" />
              <stop offset="1" stopColor="#fff" stopOpacity="0" />
              <animateTransform attributeName="gradientTransform" type="translate" from={`-260 0`} to={`${size.w + 260} 0`} dur="6s" repeatCount="indefinite" />
            </linearGradient>
            <filter id="hotrod-glow" x="-5%" y="-20%" width="110%" height="140%">
              <feGaussianBlur stdDeviation="4" />
            </filter>
          </defs>
          <path d={d} fill="#ff2d95" opacity="0.55" filter="url(#hotrod-glow)" />
          <path d={d} fill="url(#hotrod-paint)" />
          <path d={d} fill="url(#hotrod-glint)" />
          <path d={d} fill="none" stroke="#fff" strokeWidth="2.6" strokeLinejoin="round" />
        </svg>
      )}
    </div>
  );
}

// Tailwind's arbitrary classes, escaped for a selector.
const esc = (cls) => cls.replace(/([[\]#/.:])/g, '\\$1');

/** The re-colouring: every green turns hot pink, every grey a deep berry. */
export const RABEL_CSS = `
.gym-rabel { background: radial-gradient(120% 80% at 50% 0%, #4a0a33 0%, #1e0616 55%, #120310 100%) fixed !important; }
.gym-rabel .${esc('bg-[#0a0a0a]/95')} { background-color: rgba(30, 6, 22, 0.94) !important; }
.gym-rabel .${esc('bg-[#141414]')}, .gym-rabel .${esc('bg-[#0f0f0f]')} { background-color: rgba(70, 10, 48, 0.72) !important; }
.gym-rabel .${esc('bg-[#1a1a1a]')}, .gym-rabel .${esc('bg-[#1f1f1f]')} { background-color: rgba(110, 18, 74, 0.8) !important; }
.gym-rabel .${esc('border-[#232323]')}, .gym-rabel .${esc('border-[#2a2a2a]')}, .gym-rabel .${esc('border-[#262626]')},
.gym-rabel .${esc('border-[#222]')}, .gym-rabel .${esc('border-[#333]')} { border-color: rgba(255, 64, 160, 0.35) !important; }
.gym-rabel .${esc('border-[#3a3a3a]')} { border-color: rgba(255, 120, 190, 0.55) !important; }
.gym-rabel .bg-green-500, .gym-rabel .bg-green-600 { background: linear-gradient(135deg, #ff2d95, #ff6ec7 55%, #ffb3de) !important; color: #3a0024 !important; box-shadow: 0 0 18px rgba(255, 45, 149, 0.55); }
.gym-rabel .${esc('bg-green-500/10')} { background-color: rgba(255, 45, 149, 0.16) !important; }
.gym-rabel .${esc('bg-green-600/15')} { background-color: rgba(255, 45, 149, 0.2) !important; }
.gym-rabel .${esc('bg-green-950/95')} { background-color: rgba(120, 0, 70, 0.95) !important; }
.gym-rabel .text-green-400 { color: #ff6ec7 !important; }
.gym-rabel .border-green-500, .gym-rabel .border-green-700, .gym-rabel .border-green-800 { border-color: #ff2d95 !important; }
.gym-rabel .${esc('focus:border-green-500')}:focus { border-color: #ff2d95 !important; }
.gym-rabel .text-gray-400, .gym-rabel .text-gray-500 { color: #f3a6cf !important; }
.gym-rabel .text-gray-600 { color: #c77aa4 !important; }
.gym-rabel h1 { font-family: 'Pacifico', cursive; font-weight: 400; background: linear-gradient(90deg, #ff2d95, #ffb3de, #ff6ec7); -webkit-background-clip: text; background-clip: text; color: transparent; text-shadow: 0 0 22px rgba(255, 45, 149, 0.35); }
.gym-rabel h1 input { font-family: 'Pacifico', cursive; color: #ff9fd4; -webkit-text-fill-color: #ff9fd4; }
.gym-rabel .rabel-clock { font-family: 'Pacifico', cursive; font-weight: 400; color: #ffd6ec !important; text-shadow: 0 0 20px #ff2d95; }
.gym-rabel .gym-content { position: relative; z-index: 1; }
.rabel-toggle-on { background: linear-gradient(135deg, #ff2d95, #ff6ec7); border-color: #ff9fd4; color: #fff; box-shadow: 0 0 14px rgba(255, 45, 149, 0.6); }
.rabel-glow { background: radial-gradient(60% 100% at 50% 100%, rgba(255, 45, 149, 0.45), transparent 70%); }
.rabel-hood { transform-origin: 50% 100%; animation: rabel-hood 3.4s ease-in-out infinite alternate; }
@keyframes rabel-hood { from { transform: scaleY(0.96); } to { transform: scaleY(1.03); } }
@media (prefers-reduced-motion: reduce) { .rabel-hood { animation: none; } }
.rabel-bit { animation: rabel-pop 1.1s cubic-bezier(.2,.8,.3,1) forwards; }
@keyframes rabel-pop { 0% { transform: translate(0, 0) scale(0.3); opacity: 1; } 70% { opacity: 1; } 100% { transform: translate(var(--dx), var(--dy)) scale(1.3) rotate(25deg); opacity: 0; } }
.rabel-sass { font-family: 'Pacifico', cursive; text-shadow: 0 0 16px #ff2d95, 0 0 32px #ff2d95; animation: rabel-sass 1.3s ease-out forwards; }
@keyframes rabel-sass { 0% { transform: scale(0.4) rotate(-8deg); opacity: 0; } 25% { transform: scale(1.15) rotate(-4deg); opacity: 1; } 75% { opacity: 1; } 100% { transform: scale(1) translateY(-40px) rotate(-4deg); opacity: 0; } }
`;
